import {green} from '@material-ui/core/colors';
import {makeStyles} from '@material-ui/core/styles';
import {ClassNameMap} from '@material-ui/styles/withStyles';
import {FormCostCenterDto, FormOrderDto, Marketing} from 'Agent/AgentForms/Marketing/models/Marketing';
import {Async, AsyncPage, Empty,  isLoaded, toEither} from 'Core/models/Async';
import {FilterOptionUnion, FilterOptionValue} from 'Core/models/FilterOption';
import {Page} from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {Dispatch, SetStateAction} from 'react';
import {Sort} from 'Shared/components/PagedView/Table/Sort';
import {createAction} from 'Shared/utilities/actionCreator';
import {isUserEmulating} from 'Shared/utilities/impersonationHelpers';
import {CostCenterProps} from '../components/CostCenterInput';

export enum MarketingActionType {
  GET_MARKETING_FORM = 'marketing/GET_MARKETING',
  GET_COST_CENTERS = 'marketing/GET_COST_CENTERS',
  CHANGER_MARKETING_FORM = 'marketing/CHANGE_MARKETING',
  CHANGE_MARKETING_FILTERS = 'marketing/CHANGE_MARKETING_FILTER',
  CHANGE_SORT = 'marketing/CHANGE_SORT',
  CLEAR_SORT = 'marketing/CLEAR_SORT',
  CHANGE_VIEW_CART = 'marketing/CHANGE_VIEW_CART',
  CLEAR_VIEW_CART = 'marketing/CLEAR_VIEW_CART',
  CLEAR_MARKETING = 'marketing/CLEAR_MARKETING',
  CHANGE_CART_VISIBILITY = 'marketing/CHANGE_CART_VISIBILITY',
  CHANGE_IS_SHIPPING_TO_SELF = 'marketing/CHANGE_IS_SHIPPING_TO_SELF',
  CHANGE_IS_SHIPPING_PRIORITY = 'marketing/CHANGE_IS_SHIPPING_PRIORITY',
  CHANGE_MISSING_REQUIRED_FIELDS = 'marketing/CHANGE_MISSING_REQUIRED_FIELDS',
  VALIDATE_ADDRESS = 'marketing/VALIDATE_ADDRESS',
  ADDRESS_IS_VALIDATING = 'marketing/ADDRESS_IS_VALIDATING',
  CHANGE_VALIDATE_ADDRESS = 'marketing/CHANGE_VALIDATE_ADDRESS',
  SUBMIT_FORM_ORDER = 'marketing/SUBMIT_FORM_ORDER',
  CHANGE_FORM_ORDER = 'marketing/CHANGE_FORM_ORDER',
  CHANGE_CHECKOUT_CART = 'marketing/CHANGE_CHECKOUT_CART',
  CLEAR_CHECKOUT_CART = 'marketing/CLEAR_CHECKOUT_CART',
  CHANGE_COST_CENTERS = 'marketing/CHANGE_COST_CENTERS',
  CHANGE_SELECTED_COST_CENTER = 'marketing/CHANGE_SELECTED_COST_CENTER',
  CHANGE_UNVALIDATED_ADDRESS_ACCEPTED = 'marketing/CHANGE_UNVALIDATED_ADDRESS_ACCEPTED',
  CHANGE_VALID_ADDRESS_SELECTED = 'marketing/CHANGE_VALID_ADDRESS_SELECTED,',
  CHANGE_IS_PO_BOX_ADDRESS = 'marketing/CHANGE_IS_PO_BOX_ADDRESS',
}

export type CheckoutCart = {
  items: Map<string, number>;
};

export const EmptycheckoutCart: CheckoutCart = {
  items: new Map<string, number>(),
};

export enum RequiredCheckoutCartFields {
  FIRST_NAME = 'First Name ',
  LAST_NAME = 'Last Name ',
  ADDRESS_LINE_1 = 'Address Line 1 ',
  CITY = 'City ',
  ZIP = 'Zip ',
  STATE = 'State ',
  PHONE = 'Phone ',
  FEDEX_NAME = 'Fed Ex Name ',
  FEDEX_ACCOUNT_NUMBER = 'Fed Ex Account Number ',
  EMAIL = 'YourEmail',
  COST_CENTER = 'Cost Center',
}

export type MarketingFilters = {
  category: FilterOptionUnion;
  categorySelected: string;
  formNumber: string;
  type: FilterOptionUnion;
  states: FilterOptionUnion;
  statesSelected: string;
  title: string;
  description: string;
  pageNumber: string;
  pageSize: string;
  titleOrType: string;
};

export type FilterEnum = {
  name: string;
  value: string;
};

export type MarketingViewCart = {
  items: Map<string, Marketing>;
  company?: string;
  firstName?: string;
  lastName?: string;
  accountNumber?: string;
  costCenter?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  email?: string;
  phoneNumber?: string;
  fedExName?: string;
  fedExNumber?: string;
  instructions?: string;
  shipPriority?: string;
  isShippingToSelf?: boolean;
  missingRequiredFields: string[];
};

export type CartItemVisibility = {
  inCartFormNumber: string[];
};

export const EmptyCartItemVisibility: CartItemVisibility = {
  inCartFormNumber: [],
};

export const EmptyMarketingFilter: MarketingFilters = {
  category: FilterOptionValue.EmptyOption(),
  categorySelected: '',
  formNumber: '',
  type: FilterOptionValue.EmptyOption(),
  states: FilterOptionValue.EmptyOption(),
  statesSelected: '',
  title: '',
  description: '',
  pageNumber: '1',
  pageSize: '25',
  titleOrType: '',
};

export const DEFAULT_FORM_NUMBER: string = 'DONT-USE';
export const DEFAULT_FORM_ID: string = '0';
export const EmptyCostCenter: FormCostCenterDto = {};

export const EmptyMarketingViewCart: MarketingViewCart = {
  missingRequiredFields: [],
  address: '',
  address2: '',
  city: '',
  fedExName: '',
  fedExNumber: '',
  instructions: '',
  isShippingToSelf: true,
  phoneNumber: '',
  state: '',
  zip: '',
  email: '',
  items: new Map<string, Marketing>(),
  company: '',
  firstName: '',
  lastName: '',
  accountNumber: '',
  costCenter: '',
  shipPriority: 'Standard',
};

export const EmptyFormOrder: FormOrderDto = {};

export const changeMarketingFilters = (filters: MarketingFilters) =>
  createAction(MarketingActionType.CHANGE_MARKETING_FILTERS, {filters});

export const clearMarketing = (filters: MarketingFilters) =>
  createAction(MarketingActionType.CLEAR_MARKETING, {filters});

export const getMarketing = (page?: Page<Marketing>, filter?: MarketingFilters, sort?: Sort, isNewYork?: boolean) =>
  createAction(MarketingActionType.GET_MARKETING_FORM, {
    page,
    filter,
    sort,
    isNewYork,
  });

export const getCostCenters = () => createAction(MarketingActionType.GET_COST_CENTERS);
export const changeCostCenters = (costCenters: Async<FormCostCenterDto[]>) =>
  createAction(MarketingActionType.CHANGE_COST_CENTERS, {costCenters});

export const changeSelectedCostCenter = (selectedCostCenter: FormCostCenterDto) =>
  createAction(MarketingActionType.CHANGE_SELECTED_COST_CENTER, {selectedCostCenter});

export const changeSort = (sort?: Sort) => createAction(MarketingActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(MarketingActionType.CLEAR_SORT);
export const changeViewCart = (viewCart: MarketingViewCart) =>
  createAction(MarketingActionType.CHANGE_VIEW_CART, {viewCart});
export const clearViewCart = () => createAction(MarketingActionType.CLEAR_VIEW_CART);
export const changeMarketing = (marketing: AsyncPage<Page<Marketing>>) =>
  createAction(MarketingActionType.CHANGER_MARKETING_FORM, marketing);
export const changeCartVisibility = (visibility: CartItemVisibility) =>
  createAction(MarketingActionType.CHANGE_CART_VISIBILITY, {visibility});
export const changeShippingToSelf = (isShippingToSelf: boolean) =>
  createAction(MarketingActionType.CHANGE_IS_SHIPPING_TO_SELF, {isShippingToSelf});
export const changeMissingRequiredFields = (missingRequiredFields: string[]) =>
  createAction(MarketingActionType.CHANGE_MISSING_REQUIRED_FIELDS, {missingRequiredFields});
export const changeShippingPriority = (shippingPriority: string) =>
  createAction(MarketingActionType.CHANGE_IS_SHIPPING_PRIORITY, {shippingPriority});
export const validateAddress = (viewCart: MarketingViewCart, missingRequiredFields: string[]) =>
  createAction(MarketingActionType.VALIDATE_ADDRESS, {
    viewCart,
    missingRequiredFields,
  });
export const addressIsValidating = (isValidating: boolean) =>
  createAction(MarketingActionType.ADDRESS_IS_VALIDATING, {isValidating});
export const changeValidateAddress = (address: Async<AddressResponse>) =>
  createAction(MarketingActionType.CHANGE_VALIDATE_ADDRESS, {address});
export const submitFormOrder = (viewCart: MarketingViewCart) =>
  createAction(MarketingActionType.SUBMIT_FORM_ORDER, {viewCart});
export const changeFormOrder = (formOrder: Async<FormOrderDto>) =>
  createAction(MarketingActionType.CHANGE_FORM_ORDER, {formOrder});
export const changeCheckoutCart = (checkoutItems: CheckoutCart) =>
  createAction(MarketingActionType.CHANGE_CHECKOUT_CART, {checkoutItems});
export const clearCheckoutCart = () => createAction(MarketingActionType.CLEAR_CHECKOUT_CART);
export const changeUnValidatedAddressAccepted = (unValidatedAddressAccepted: boolean) =>
  createAction(MarketingActionType.CHANGE_UNVALIDATED_ADDRESS_ACCEPTED, {unValidatedAddressAccepted});
export const changeValidAddressSelected = (validAddressSelected: boolean) =>
  createAction(MarketingActionType.CHANGE_VALID_ADDRESS_SELECTED, {validAddressSelected});
export const changeIsPOBoxAddress = (isPOBoxAddress: boolean) =>
  createAction(MarketingActionType.CHANGE_IS_PO_BOX_ADDRESS, {isPOBoxAddress});

type MarketingState = {
  marketingFormsCategories: FilterEnum[];
  marketingFormsType: FilterEnum[];
  marketing: AsyncPage<Page<Marketing>>;
  filters: MarketingFilters;
  sort: O.Option<Sort>;
  viewCart: MarketingViewCart;
  inCartVisible: CartItemVisibility;
  isShippingToSelf: boolean;
  missingRequiredFields: string[];
  shippingPriority: string;
  addressValidationResponse: Async<AddressResponse>;
  isAddressValidating: boolean;
  formOrder: Async<FormOrderDto>;
  costCenters: Async<FormCostCenterDto[]>;
  selectedCostCenter?: FormCostCenterDto;
  checkoutCart: CheckoutCart;
  unValidatedAddressAccepted: boolean;
  validAddressSelected: boolean;
  isPOBoxAddress: boolean;
};

export type AddressResponse = {
  validationMessage: string;
  addressValidatedCode: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
};

export const DEFAULT_STATE: MarketingState = {
  marketingFormsCategories: [],
  marketingFormsType: [],
  marketing: new Empty(),
  filters: EmptyMarketingFilter,
  sort: O.fromNullable({column: 'title', direction: 'asc'}),
  viewCart: EmptyMarketingViewCart,
  inCartVisible: EmptyCartItemVisibility,
  isShippingToSelf: true,
  missingRequiredFields: [],
  shippingPriority: 'Standard',
  addressValidationResponse: new Empty(),
  isAddressValidating: false,
  formOrder: new Empty(),
  costCenters: new Empty(),
  selectedCostCenter: EmptyCostCenter,
  checkoutCart: EmptycheckoutCart,
  unValidatedAddressAccepted: false,
  validAddressSelected: false,
  isPOBoxAddress: false,
};

type MarketingAction =
  | ReturnType<typeof getMarketing>
  | ReturnType<typeof clearSort>
  | ReturnType<typeof clearMarketing>
  | ReturnType<typeof changeMarketing>
  | ReturnType<typeof changeSort>
  | ReturnType<typeof changeMarketingFilters>
  | ReturnType<typeof changeViewCart>
  | ReturnType<typeof clearViewCart>
  | ReturnType<typeof changeCartVisibility>
  | ReturnType<typeof changeShippingToSelf>
  | ReturnType<typeof changeMissingRequiredFields>
  | ReturnType<typeof changeShippingPriority>
  | ReturnType<typeof validateAddress>
  | ReturnType<typeof addressIsValidating>
  | ReturnType<typeof changeValidateAddress>
  | ReturnType<typeof submitFormOrder>
  | ReturnType<typeof changeFormOrder>
  | ReturnType<typeof changeCheckoutCart>
  | ReturnType<typeof clearCheckoutCart>
  | ReturnType<typeof getCostCenters>
  | ReturnType<typeof changeSelectedCostCenter>
  | ReturnType<typeof changeCostCenters>
  | ReturnType<typeof changeUnValidatedAddressAccepted>
  | ReturnType<typeof changeValidAddressSelected>
  | ReturnType<typeof changeIsPOBoxAddress>;

export default (state: MarketingState , action: MarketingAction): MarketingState => {
  const currentState = state || DEFAULT_STATE;
  switch (action.type) {
    case MarketingActionType.CHANGER_MARKETING_FORM:
      return {
        ...currentState,
        marketing: action.payload,
        marketingFormsCategories: isLoaded(action.payload) ? action.payload.Data.marketingFormsCategories! : [],
        marketingFormsType: isLoaded(action.payload) ? action.payload.Data.marketingFormsType! : [],
      };
    case MarketingActionType.CHANGE_MARKETING_FILTERS:
      action.payload.filters.pageNumber = '1';
      return {
        ...currentState,
        filters: action.payload.filters,
      };
    case MarketingActionType.CLEAR_SORT:
      state.filters.pageNumber = '1';
      return {
        ...currentState,
        sort: DEFAULT_STATE.sort,
      };
    case MarketingActionType.CHANGE_SORT:
      state.filters.pageNumber = '1';
      return {
        ...currentState,
        sort: O.fromNullable(action.payload.sort),
      };
    case MarketingActionType.CLEAR_MARKETING:
      state.filters = EmptyMarketingFilter;
      return {
        ...currentState,
        filters: action.payload.filters,
      };
    case MarketingActionType.CLEAR_VIEW_CART:
      return {
        ...currentState,
        viewCart: DEFAULT_STATE.viewCart,
      };
    case MarketingActionType.CHANGE_VIEW_CART:
      return {
        ...currentState,
        viewCart: action.payload.viewCart,
      };
    case MarketingActionType.CHANGE_CART_VISIBILITY:
      return {
        ...currentState,
        inCartVisible: action.payload.visibility,
      };
    case MarketingActionType.CHANGE_IS_SHIPPING_TO_SELF:
      return {
        ...currentState,
        isShippingToSelf: action.payload.isShippingToSelf,
      };
    case MarketingActionType.CHANGE_MISSING_REQUIRED_FIELDS:
      return {
        ...currentState,
        missingRequiredFields: action.payload.missingRequiredFields,
      };
    case MarketingActionType.CHANGE_IS_SHIPPING_PRIORITY:
      return {
        ...currentState,
        shippingPriority: action.payload.shippingPriority,
      };
    case MarketingActionType.VALIDATE_ADDRESS:
      return {
        ...currentState,
        viewCart: action.payload.viewCart,
        missingRequiredFields: action.payload.missingRequiredFields,
      };
    case MarketingActionType.ADDRESS_IS_VALIDATING:
      return {
        ...currentState,
        isAddressValidating: action.payload.isValidating,
      };
    case MarketingActionType.CHANGE_VALIDATE_ADDRESS:
      return {
        ...currentState,
        addressValidationResponse: action.payload.address,
      };
    case MarketingActionType.SUBMIT_FORM_ORDER:
      return {
        ...currentState,
        viewCart: action.payload.viewCart,
      };
    case MarketingActionType.CHANGE_FORM_ORDER:
      return {
        ...currentState,
        formOrder: action.payload.formOrder,
      };

    case MarketingActionType.CHANGE_COST_CENTERS:
      return {
        ...currentState,
        costCenters: action.payload.costCenters,
      };

    case MarketingActionType.CHANGE_SELECTED_COST_CENTER:
      return {
        ...currentState,
        selectedCostCenter: action.payload.selectedCostCenter,
      };
    case MarketingActionType.CHANGE_CHECKOUT_CART:
      return {
        ...currentState,
        checkoutCart: action.payload.checkoutItems,
      };
    case MarketingActionType.CLEAR_CHECKOUT_CART:
      return {
        ...currentState,
        checkoutCart: DEFAULT_STATE.checkoutCart,
      };
    case MarketingActionType.CHANGE_UNVALIDATED_ADDRESS_ACCEPTED:
      return {
        ...currentState,
        unValidatedAddressAccepted: action.payload.unValidatedAddressAccepted,
      };
      case MarketingActionType.CHANGE_VALID_ADDRESS_SELECTED:
      return {
        ...currentState,
        validAddressSelected: action.payload.validAddressSelected,
      };
    case MarketingActionType.CHANGE_IS_PO_BOX_ADDRESS:
      return {
        ...currentState,
        isPOBoxAddress: action.payload.isPOBoxAddress,
      };
    default:
      return currentState;
  }
};

export const useGreenAlertStyles = makeStyles(theme => ({
  button: {
    marginTop: theme.spacing(4),
    marginRight: theme.spacing(1),
  },
  disabledButton: {
    marginTop: theme.spacing(4),
    marginRight: theme.spacing(1),
    cursor: 'not-allowed',
    backgroundColor: 'rgb(229, 229, 229)',
  },
  finalActionsContainer: {
    marginBottom: theme.spacing(2),
    justifyContent: 'center',
  },
  resetContainer: {
    animation: 'slideInBottom .75s',
    padding: theme.spacing(3),
  },
  greenAlert: {
    color: 'white',
    backgroundColor: green[500],
    padding: '0',
    paddingLeft: '85px',
  },
}));

export function handleFieldValidation(enumValue: any, field: string | undefined, missingRequiredFields: string[]) {
  if (enumValue === RequiredCheckoutCartFields.EMAIL && field && !looksLikeMail(field)) {
    missingRequiredFields.push(enumValue);
  } else if (!field || field?.length < 1) {
    missingRequiredFields.push(enumValue);
  } else if (missingRequiredFields.indexOf(enumValue.toString()) > -1) {
    let indexToRemove = missingRequiredFields.indexOf(enumValue.toString());
    while (indexToRemove > -1) {
      missingRequiredFields.splice(indexToRemove, 1);
      indexToRemove = missingRequiredFields.indexOf(enumValue.toString());
    }
  }
}

export function isPOBox(address: string): boolean {
  return (
    /\bP\.O/i.test(address) ||
    /\bPO\d+\b/i.test(address) ||
    /\bP\.?\s*O\.?\s*(BOX|BIN|B\.?)\b/i.test(address) ||
    /\bP\.O\.?\s*(BOX|BIN|B\.?)\b/i.test(address) ||
    /\bPost\s+Office\s+Box\b/i.test(address) ||
    /\bP\.?\s*O\.?\s+\d+\b/i.test(address) ||
    /\bP\.O\.?\s+\d+\b/i.test(address) ||
    /\bbox\s*\d+\b/i.test(address) ||
    /\bbox\b/i.test(address)
  );
}

export function looksLikeMail(strEmail: string) {
  const lastAtPos = strEmail.lastIndexOf('@');
  const lastDotPos = strEmail.lastIndexOf('.');
  return (
    lastAtPos < lastDotPos &&
    lastAtPos > 0 &&
    strEmail.indexOf('@@') == -1 &&
    lastDotPos > 2 &&
    strEmail.length - lastDotPos > 2
  );
}

export function isAddressValidated(addressValidationResponse: Async<AddressResponse>) {
  if (addressValidationResponse.key !== 'Loaded') {
    return false;
  } else {
    return pipe(
      toEither(addressValidationResponse),
      E.fold(
        () => {
          return false;
        },
        address => {
          if (address && address.addressValidatedCode === '0002') return true;
        }
      )
    );
  }
}

export function addressIsChanged(cart: MarketingViewCart, addressValidationResponse: Async<AddressResponse>) {
  if (addressValidationResponse.key !== 'Loaded') {
    return true;
  } else {
    return pipe(
      toEither(addressValidationResponse),
      E.fold(
        () => {
          return true;
        },
        address => {
          if (
            address &&
            address.address === cart.address &&
            address.address2 === cart.address2 &&
            address.zip === cart.zip &&
            address.state === cart.state &&
            address.city === cart.city
          ) {
            return false;
          } else {
            return true;
          }
        }
      )
    );
  }
}

export function addressSelection(addressValidationResponse: Async<AddressResponse>) {
  return pipe(
    toEither(addressValidationResponse),
    E.fold(
      () => {
        return '';
      },
      address => {
        if (address.address2 === null || address.address2.length > 1) {
          return address.address + ', ' + address.city + ', ' + address.state + ' ' + address.zip;
        } else {
          return (
            address.address + ': ' + address.address2 + ', ' + address.city + ', ' + address.state + ' ' + address.zip
          );
        }
      }
    )
  );
}

export const incrementedAmount = (value: any, currentFormIncrement: number) => {
  if (value % currentFormIncrement > 0) {
    return Math.floor(value / currentFormIncrement) * currentFormIncrement + currentFormIncrement;
  } else {
    return value;
  }
};

export const cartValue = (checkoutCart: CheckoutCart, currentFormNumber: string, viewCart: MarketingViewCart) => {
  if (checkoutCart?.items?.get(currentFormNumber) !== undefined) {
    return checkoutCart.items.get(currentFormNumber);
  } else {
    if (
      viewCart.items.get(currentFormNumber) !== undefined &&
      viewCart.items.get(currentFormNumber)?.quantity !== undefined
    ) {
      return viewCart.items.get(currentFormNumber)?.quantity;
    } else {
      return 0;
    }
  }
};

export const currentQuantity = (checkoutCart: CheckoutCart, currentFormNumber: string, viewCart: MarketingViewCart) => {
  let data: number | undefined = cartValue(checkoutCart, currentFormNumber, viewCart);
  if (data !== undefined) {
    return data;
  } else {
    return 0;
  }
};

export const validateOrderAmount = (
  passedValue: any,
  currentFormMin: number,
  currentFormMax: number,
  currentFormIncrement: number
) => {
  let quantity = incrementedAmount(passedValue, currentFormIncrement);
  let value = passedValue;

  if (quantity != value) {
    value = quantity;
  }
  if (!isNaN(value) && Number(value) < currentFormMin) {
    value = currentFormMin;
  }
  if (!isNaN(value) && Number(value) > currentFormMax) {
    value = currentFormMax;
  }
  return value;
};

export const resetOrderAmount = (
  value: any,
  currentFormNumber: string,
  marketing: Marketing,
  checkoutCart: CheckoutCart,
  setCheckoutCart: Dispatch<SetStateAction<CheckoutCart>>,
  viewCart: MarketingViewCart,
  setViewCart: Dispatch<SetStateAction<MarketingViewCart>>
) => {
  let data: Marketing | undefined = viewCart.items.get(currentFormNumber);
  if (data !== undefined) {
    viewCart.items.set(currentFormNumber, {...marketing, quantity: value});
  } else {
    checkoutCart.items.set(currentFormNumber, value);
    setCheckoutCart({...checkoutCart});
  }
  setViewCart({...viewCart});
};

export const setCartAddress = (
  e: any,
  setCart: Dispatch<SetStateAction<MarketingViewCart>>,
  cart: MarketingViewCart
) => {
  if (e.target.name == 'address') {
    setCart({...cart, address: e.target.value});
  } else if (e.target.name == 'address2') {
    setCart({...cart, address2: e.target.value});
  } else if (e.target.name == 'city') {
    setCart({...cart, city: e.target.value});
  } else if (e.target.name == 'state') {
    setCart({...cart, state: e.target.value});
  } else if (e.target.name == 'zip') {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '');
    if (onlyNums.length <= 5) {
      setCart({...cart, ['zip']: onlyNums});
    } else if (onlyNums.length <= 9) {
      const value = onlyNums.substring(0, 5) + '-' + onlyNums.substring(5, onlyNums.length);
      setCart({...cart, ['zip']: value});
    }
  }
};

export const setCartPhoneAndEmail = (
  e: any,
  setCart: Dispatch<SetStateAction<MarketingViewCart>>,
  cart: MarketingViewCart,
  missingRequiredFields: string[],
  dispatch: Dispatch<any>
) => {
  if (e.target.name === 'phoneNumber') {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '');
    if (onlyNums.length < 10) {
      setCart({...cart, phoneNumber: onlyNums});
    } else if (onlyNums.length === 10) {
      setCart({
        ...cart,
        phoneNumber: onlyNums.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3'),
      });
    }
    handleFieldValidation(RequiredCheckoutCartFields.PHONE, cart.phoneNumber, missingRequiredFields);
    dispatch(changeMissingRequiredFields(missingRequiredFields));
  }
  if (e.target.name === 'email') {
    const emailValue = e.target.value;
    setCart({...cart, email: emailValue});
    handleFieldValidation(RequiredCheckoutCartFields.EMAIL, emailValue, missingRequiredFields);
    dispatch(changeMissingRequiredFields(missingRequiredFields));
  }
};

export const selectAddress = (
  addressValidationResponse: Async<AddressResponse>,
  setCart: Dispatch<SetStateAction<MarketingViewCart>>,
  cart: MarketingViewCart,
  setAddressConfirmed: Dispatch<SetStateAction<boolean>>
) => {
  pipe(
    toEither(addressValidationResponse),
    E.fold(
      () => {},
      address => {
        setCart({
          ...cart,
          address: address.address,
          address2: address.address2,
          city: address.city,
          state: address.state,
          zip: address.zip,
        });
        return setAddressConfirmed(true);
      }
    )
  );
};

export function removeCartItems(
  inCartVisible: CartItemVisibility,
  cart: MarketingViewCart,
  setCart: Dispatch<SetStateAction<MarketingViewCart>>
) {
  while (inCartVisible.inCartFormNumber.length > 0) {
    inCartVisible.inCartFormNumber.pop();
  }
  cart.items.clear();
  setCart({...cart});
}

export const StateFilter: FilterEnum[] = [
  {
    name: 'Alabama',
    value: 'AL',
  },
  {
    name: 'Alaska',
    value: 'AK',
  },
  {
    name: 'American Samoa',
    value: 'AS',
  },
  {
    name: 'Arizona',
    value: 'AZ',
  },
  {
    name: 'Arkansas',
    value: 'AR',
  },
  {
    name: 'California',
    value: 'CA',
  },
  {
    name: 'Colorado',
    value: 'CO',
  },
  {
    name: 'Connecticut',
    value: 'CT',
  },
  {
    name: 'Delaware',
    value: 'DE',
  },
  {
    name: 'District of Columbia',
    value: 'DC',
  },
  {
    name: 'Florida',
    value: 'FL',
  },
  {
    name: 'Georgia',
    value: 'GA',
  },
  {
    name: 'Guam',
    value: 'GU',
  },
  {
    name: 'Hawaii',
    value: 'HI',
  },
  {
    name: 'Idaho',
    value: 'ID',
  },
  {
    name: 'Illinois',
    value: 'IL',
  },
  {
    name: 'Indiana',
    value: 'IN',
  },
  {
    name: 'Iowa',
    value: 'IA',
  },
  {
    name: 'Kansas',
    value: 'KS',
  },
  {
    name: 'Kentucky',
    value: 'KY',
  },
  {
    name: 'Louisiana',
    value: 'LA',
  },
  {
    name: 'Maine',
    value: 'ME',
  },
  {
    name: 'Maryland',
    value: 'MD',
  },
  {
    name: 'Massachusetts',
    value: 'MA',
  },
  {
    name: 'Michigan',
    value: 'MI',
  },
  {
    name: 'Minnesota',
    value: 'MN',
  },
  {
    name: 'Mississippi',
    value: 'MS',
  },
  {
    name: 'Missouri',
    value: 'MO',
  },
  {
    name: 'Montana',
    value: 'MT',
  },
  {
    name: 'Nebraska',
    value: 'NE',
  },
  {
    name: 'Nevada',
    value: 'NV',
  },
  {
    name: 'New Hampshire',
    value: 'NH',
  },
  {
    name: 'New Jersey',
    value: 'NJ',
  },
  {
    name: 'New Mexico',
    value: 'NM',
  },
  {
    name: 'New York',
    value: 'NY',
  },
  {
    name: 'North Carolina',
    value: 'NC',
  },
  {
    name: 'North Dakota',
    value: 'ND',
  },
  {
    name: 'Northern Mariana Islands',
    value: 'MP',
  },
  {
    name: 'Ohio',
    value: 'OH',
  },
  {
    name: 'Oklahoma',
    value: 'OK',
  },
  {
    name: 'Oregon',
    value: 'OR',
  },
  {
    name: 'Pennsylvania',
    value: 'PA',
  },
  {
    name: 'Puerto Rico',
    value: 'PR',
  },
  {
    name: 'Rhode Island',
    value: 'RI',
  },
  {
    name: 'South Carolina',
    value: 'SC',
  },
  {
    name: 'South Dakota',
    value: 'SD',
  },
  {
    name: 'Tennessee',
    value: 'TN',
  },
  {
    name: 'Texas',
    value: 'TX',
  },
  {
    name: 'Utah',
    value: 'UT',
  },
  {
    name: 'Vermont',
    value: 'VT',
  },
  {
    name: 'Virginia',
    value: 'VA',
  },
  {
    name: 'Washington',
    value: 'WA',
  },
  {
    name: 'West Virginia',
    value: 'WV',
  },
  {
    name: 'Wisconsin',
    value: 'WI',
  },
  {
    name: 'Wyoming',
    value: 'WY',
  },
  {
    name: 'U.S. Virgin Islands',
    value: 'VI',
  },
];

export function onChangeCostCenter(dispatch: Dispatch<any>, props: CostCenterProps): (e: any, newValue: any) => void {
  return (e, newValue) => {
    if (newValue !== null) {
      dispatch(changeSelectedCostCenter(newValue));
      props.setHandler(e, `${newValue.costCenter} - ${newValue.costCenterDescription}`);
    } else {
      dispatch(changeSelectedCostCenter(EmptyCostCenter));
      props.setHandler(e, '');
    }
  };
}

export function changeInAddressFields(changedViewCart: MarketingViewCart, orignalViewCart: MarketingViewCart) {
  if (changedViewCart == undefined || orignalViewCart == undefined) {
    return true;
  } else {
    if (JSON.stringify(changedViewCart) === JSON.stringify(orignalViewCart)) {
      return false;
    }
    return true;
  }
}

export function cartDataCopyHandlerShippingDetails(
  setCartDataCopy: React.Dispatch<React.SetStateAction<MarketingViewCart>>,
  cartDataCopy: MarketingViewCart
) {
  return (e: any) => {
    if (e.target.name === 'fedExName') {
      setCartDataCopy({...cartDataCopy, fedExName: e.target.value});
    }
    if (e.target.name === 'fedExNumber') {
      setCartDataCopy({...cartDataCopy, fedExNumber: e.target.value});
    }
    if (e.target.name === 'instructions') {
      setCartDataCopy({...cartDataCopy, instructions: e.target.value});
    }
  };
}

export const handleStepBack = (
  dispatch: React.Dispatch<any>,
  changeMissingRequiredFields: any,
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
) => {
  dispatch(changeMissingRequiredFields([]));
  setActiveStep(prevActiveStep => prevActiveStep - 1);
};

export const getStepClassName = (
  activeStep: number,
  viewCartDetails: MarketingViewCart,
  unValidatedAddressAccepted: boolean,
  isPOBoxAddress: boolean,
  addressValidationResponse: Async<AddressResponse>,
  classes: ClassNameMap<'root' | 'button' | 'disabledButton' | 'actionsContainer'>
) => {
  if (activeStep === 2) {
    if (viewCartDetails.items.size >= 1) {
      return unValidatedAddressAccepted && !(isPOBoxAddress && isAddressValidated(addressValidationResponse)) ? classes.button : classes.disabledButton;
    } else {
      return classes.disabledButton;
    }
  } else {
    return viewCartDetails.items.size >= 1 ? classes.button : classes.disabledButton;
  }
};

export const stepHandleNext = (
  dispatch: React.Dispatch<any>,
  changeMissingRequiredFields: any,
  activeStep: number,
  missingRequiredFields: string[],
  viewCartDetails: MarketingViewCart,
  setActiveStep: React.Dispatch<React.SetStateAction<number>>,
  shippingPriority: string,
  userClaims: any
) => {
  dispatch(changeMissingRequiredFields([]));
  if (activeStep === 1) {
    handleFieldValidation(RequiredCheckoutCartFields.LAST_NAME, viewCartDetails.lastName, missingRequiredFields);
    handleFieldValidation(RequiredCheckoutCartFields.FIRST_NAME, viewCartDetails.firstName, missingRequiredFields);
    if (isUserEmulating(userClaims)) {
      handleFieldValidation(RequiredCheckoutCartFields.COST_CENTER, viewCartDetails.costCenter, missingRequiredFields);
    }
    dispatch(changeMissingRequiredFields(missingRequiredFields));
  }
  if (activeStep === 2) {
    handleFieldValidation(RequiredCheckoutCartFields.ADDRESS_LINE_1, viewCartDetails.address, missingRequiredFields);
    handleFieldValidation(RequiredCheckoutCartFields.CITY, viewCartDetails.city, missingRequiredFields);
    handleFieldValidation(RequiredCheckoutCartFields.ZIP, viewCartDetails.zip, missingRequiredFields);
    handleFieldValidation(RequiredCheckoutCartFields.STATE, viewCartDetails.state, missingRequiredFields);
    handleFieldValidation(RequiredCheckoutCartFields.PHONE, viewCartDetails.phoneNumber, missingRequiredFields);
    handleFieldValidation(RequiredCheckoutCartFields.EMAIL, viewCartDetails.email, missingRequiredFields);
    dispatch(changeMissingRequiredFields(missingRequiredFields));
  }
  if (activeStep === 3) {
    if (shippingPriority !== undefined && (shippingPriority === 'Overnight' || shippingPriority === '2nd Day Air')) {
      handleFieldValidation(RequiredCheckoutCartFields.FEDEX_NAME, viewCartDetails.fedExName, missingRequiredFields);
      handleFieldValidation(
        RequiredCheckoutCartFields.FEDEX_ACCOUNT_NUMBER,
        viewCartDetails.fedExNumber,
        missingRequiredFields
      );
      dispatch(changeMissingRequiredFields(missingRequiredFields));
    } else {
      let fedName = missingRequiredFields.indexOf(RequiredCheckoutCartFields.FEDEX_NAME.toString());
      let fedAccount = missingRequiredFields.indexOf(RequiredCheckoutCartFields.FEDEX_ACCOUNT_NUMBER.toString());
      missingRequiredFields.splice(fedAccount, 1);
      missingRequiredFields.splice(fedName, 1);
      dispatch(changeMissingRequiredFields(missingRequiredFields));
    }
  }
  if (missingRequiredFields.length === undefined || missingRequiredFields.length <= 0) {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  }
};

export const handleSubmitOrder = (
  dispatch: React.Dispatch<any>,
  setActiveStep: React.Dispatch<React.SetStateAction<number>>,
  setIsOrderSubmitted: React.Dispatch<React.SetStateAction<boolean>>,
  viewCart: MarketingViewCart,
  inCartVisible: CartItemVisibility,
  updatedCart: any,
  setUpdatedCart: React.Dispatch<React.SetStateAction<MarketingViewCart>>
) => {
  dispatch(submitFormOrder(viewCart));
  removeCartItems(inCartVisible, updatedCart, setUpdatedCart);
  dispatch(changeCartVisibility({...inCartVisible}));
  dispatch(clearViewCart());
  dispatch(changeShippingPriority('Standard'));
  dispatch(changeSelectedCostCenter(EmptyCostCenter));
  dispatch(changeShippingToSelf(true));
  setIsOrderSubmitted(submitted => !submitted);
  setActiveStep(prevActiveStep => prevActiveStep + 1);
};

export function onBlurChangeHandler(
  setCartDataCopy: React.Dispatch<React.SetStateAction<MarketingViewCart>>,
  cartDataCopy: MarketingViewCart,
  dispatch: Dispatch<any>
) {
  return (e: any) => {
    setCartDataCopy(cartDataCopy);
    dispatch(changeViewCart({...cartDataCopy}));
  };
}

export function cartDataCopyHandlerViewCartYourInfo(
  setCartDataCopy: React.Dispatch<React.SetStateAction<MarketingViewCart>>,
  cartDataCopy: MarketingViewCart
) {
  return (e: any, costCenterValue?: string) => {
    if (e.target.name === 'company') {
      setCartDataCopy({...cartDataCopy, company: e.target.value});
    }
    if (e.target.name === 'firstName') {
      setCartDataCopy({...cartDataCopy, firstName: e.target.value});
    }
    if (e.target.name === 'lastName') {
      setCartDataCopy({...cartDataCopy, lastName: e.target.value});
    }
    if (e.target.name === 'accountNumber') {
      setCartDataCopy({...cartDataCopy, accountNumber: e.target.value});
    }
    if (costCenterValue !== undefined) {
      setCartDataCopy({...cartDataCopy, costCenter: costCenterValue});
    }
  };
}

export function shipToSelfHandler(
  cartDataCopy: MarketingViewCart,
  nameInfo: O.Option<{
    firstName: string;
    lastName: string;
  }>,
  isImpersonating: boolean,
  setCartDataCopy: React.Dispatch<React.SetStateAction<MarketingViewCart>>,
  dispatch: Dispatch<any>,
  missingRequiredFields: string[]
) {
  return () => {
    cartDataCopy.lastName = nameInfo._tag === 'Some' ? nameInfo.value.lastName : '';
    cartDataCopy.firstName = nameInfo._tag === 'Some' ? nameInfo.value.firstName : '';
    if (isImpersonating) {
      setCartDataCopy({...cartDataCopy, isShippingToSelf: true, costCenter: ''});
      dispatch(changeSelectedCostCenter(EmptyCostCenter));
    } else {
      setCartDataCopy({...cartDataCopy, isShippingToSelf: true});
    }
    dispatch(changeShippingToSelf(true));
    if (missingRequiredFields.length > 0) {
      missingRequiredFields.splice(0, missingRequiredFields.length);
      dispatch(changeMissingRequiredFields(missingRequiredFields));
    }
  };
}

export function shipToSomeoneElseHandler(
  isImpersonating: boolean,
  setCartDataCopy: React.Dispatch<React.SetStateAction<MarketingViewCart>>,
  cartDataCopy: MarketingViewCart,
  dispatch: Dispatch<any>,
  missingRequiredFields: string[]
) {
  return () => {
    if (isImpersonating) {
      setCartDataCopy({...EmptyMarketingViewCart, items: cartDataCopy.items, isShippingToSelf: false, costCenter: ''});
      dispatch(changeSelectedCostCenter(EmptyCostCenter));
    } else {
      setCartDataCopy({...EmptyMarketingViewCart, items: cartDataCopy.items, isShippingToSelf: false});
    }
    dispatch(changeShippingToSelf(false));
    if (missingRequiredFields.length > 0) {
      missingRequiredFields.splice(0, missingRequiredFields.length);
      dispatch(changeMissingRequiredFields(missingRequiredFields));
    }
  };
}

export function dispatchChangeHandler(
  setCartDataCopy: React.Dispatch<React.SetStateAction<MarketingViewCart>>,
  cartDataCopy: MarketingViewCart,
  dispatch: Dispatch<any>
) {
  return () => {
    setCartDataCopy(cartDataCopy);
    dispatch(changeViewCart({...cartDataCopy}));
  };
}
